import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import { useSearchContext } from '../../../providers/SearchProvider';
import { useOrderContext } from '../../../providers/OrderProvider';
import { socket } from '../../../socket';
import { saveSearchHistory, getSearchHistory, clearSearchHistory, removeSearchHistoryItem } from '../../../utils/searchHistory';
import { getCityNameByCode } from '../../../utils/cityUtils';
import formatCurrency from '../../../utils/formatCurrency';
import { getRejectedOrderEditPath } from '../../../utils/order/orderListPath';
import ApplyingModal from '../processing-order/modal/ApplyingModal';
import PrePaymentModal from '../processing-order/modal/PrePaymentModal';
import ContractModal from '../processing-order/modal/ContractModal';
import DeliveryModal from '../processing-order/modal/DeliveryModal';
import AcceptModal from '../processing-order/modal/AcceptModal';
import AccountsReceivableModal from '../processing-order/modal/AccountsReceivableModal';
import ProcessingOrderModal from '../processing-order/modal/ProcessingOrderModal';
import ApproveModal from '../processing-order/modal/ApproveModal';
import CustomerModal from './modal/CustomerModal';
import ProductModal from './modal/ProductModal';

/** 有结果工作台：每类拉取上限（与 GlobalSearchBox limit 对齐） */
const GLOBAL_SEARCH_RESULT_LIMIT = 12;

const DEFAULT_MATCH_PRIORITY = ['order', 'customer', 'product'];

const formatSearchPrice = (amount) => {
	const n = Number(amount);
	if (!Number.isFinite(n) || n <= 0) return '—';
	return formatCurrency(n);
};

/** 产品单价（含 0）；历史记录缺字段时显示 — */
const formatProductSearchPrice = (amount) => {
	if (amount === undefined || amount === null || amount === '') return '—';
	const n = Number(amount);
	if (!Number.isFinite(n)) return '—';
	return formatCurrency(n);
};

/** 产品库存件数；历史记录缺字段时显示 — */
const formatProductStockQty = (qty) => {
	if (qty === undefined || qty === null || qty === '') return '—';
	const n = Number(qty);
	if (!Number.isFinite(n)) return '—';
	return String(n);
};

/** 金额是否被关键词命中（忽略 ¥ / 逗号 / k） */
const isAmountMatchedByKeyword = (amount, keyword) => {
	if (amount == null || !keyword) return false;
	const normalized = String(keyword).replace(/[¥￥,\s]/g, '').trim();
	if (!normalized) return false;
	const kMatch = normalized.match(/^(\d+(?:\.\d+)?)k$/i);
	if (kMatch) {
		const approx = Number(kMatch[1]) * 1000;
		if (!Number.isFinite(approx)) return false;
		return Math.abs(Number(amount) - approx) <= Math.max(approx * 0.01, 1);
	}
	const digits = normalized.replace(/[^\d.]/g, '');
	if (!digits) return false;
	const price = Number(amount);
	const priceStr = String(amount);
	if (priceStr.includes(digits)) return true;
	const n = Number(digits);
	if (!Number.isFinite(n)) return false;
	if (price === n) return true;
	if (Math.floor(price) === Math.floor(n)) return true;
	if (Math.abs(price - n) < 0.5) return true;
	return false;
};

const SearchColumnCard = ({ title, count, countSuffix = '', footer = null, clearLabel = null, onClear = null, children }) => (
	<div className='global-search-column'>
		<div className='global-search-column__head'>
			<h6 className='global-search-column__title'>{title}</h6>
			<div className='global-search-column__head-aside'>
				{clearLabel && onClear ? (
					<button
						type='button'
						className='btn btn-sm btn-link global-search-column__clear'
						onClick={(e) => {
							e.stopPropagation();
							onClear();
						}}
					>
						{clearLabel}
					</button>
				) : null}
				<span className='global-search-column__count'>
					{count}
					{countSuffix}
				</span>
			</div>
		</div>
		<div className='global-search-column__body'>{children}</div>
		{footer ? <div className='global-search-column__footer'>{footer}</div> : null}
	</div>
);

const SearchColumnEmpty = ({ text }) => <div className='global-search-column__empty'>{text}</div>;

const SearchStatusPill = ({ statusBadges }) => {
	const badge = statusBadges?.payment || statusBadges?.delivery;
	if (!badge) return <span className='text-muted'>—</span>;
	return (
		<Badge bg={badge.bg} className='global-search-status-pill align-middle'>
			{badge.text}
		</Badge>
	);
};

const CustomerTypePill = ({ customerType }) => {
	if (!customerType) return <span className='text-muted'>—</span>;
	const tone = customerType === '临床' ? 'warning' : customerType === '科研' ? 'info' : 'secondary';
	return (
		<Badge bg={tone} className='global-search-status-pill align-middle'>
			{customerType}
		</Badge>
	);
};

/** 最近访问：三栏横排；有结果：按匹配优先级纵向分区 */
const SearchWorkbenchColumns = ({
	layout = 'columns',
	sectionOrder = ['order', 'customer', 'product'],
	orders,
	customers,
	products,
	selectedIndex,
	getItemGlobalIndex,
	itemsRefs,
	getOrderStatusBadges,
	highlightText,
	highlightAmount,
	onSelectOrder,
	onSelectCustomer,
	onSelectProduct,
	onRemoveHistoryItem,
	onClearCategoryHistory,
	emptyOrderText,
	emptyCustomerText,
	emptyProductText,
}) => {
	const showRemove = typeof onRemoveHistoryItem === 'function';
	const canClearCategory = typeof onClearCategoryHistory === 'function';
	const isStack = layout === 'stack';
	const hideEmpty = isStack;

	const renderOrderColumn = () => {
		if (hideEmpty && orders.length === 0) return null;
		return (
			<SearchColumnCard
				key='order'
				title='订单'
				count={orders.length}
				countSuffix=' 条'
				footer={orders.length > 0 ? `显示全部 ${orders.length} 条 · 点击行查看详情` : null}
				clearLabel={canClearCategory && orders.length > 0 ? '清空订单历史' : null}
				onClear={() => onClearCategoryHistory('orders')}
			>
				{orders.length === 0 ? (
					<SearchColumnEmpty text={emptyOrderText} />
				) : (
					<div className='global-search-table global-search-table--order'>
						{/* 设计稿列序：单号 | 客户 | 关联产品 | 备注 | 状态 | 金额 */}
						<div className='global-search-table__head global-search-table__head--order'>
							<span>单号</span>
							<span>客户</span>
							<span>关联产品</span>
							<span>备注</span>
							<span>状态</span>
							<span className='global-search-table__num'>金额</span>
						</div>
						{orders.map((order, orderIndex) => {
							const statusBadges = getOrderStatusBadges(order);
							const globalIndex = getItemGlobalIndex('order', orderIndex);
							const isSelected = selectedIndex === globalIndex;
							const relatedItems = order.relatedProducts?.length
								? order.relatedProducts
								: order.matchedProducts || [];
							const relatedNames = relatedItems
								.map((item) => {
									const code = (item.productCode || '').trim();
									const name = (item.name || '').trim();
									if (code && name) return `【${code}】${name}`;
									if (code) return `【${code}】`;
									return name;
								})
								.filter(Boolean);
							const relatedPreview = relatedNames.slice(0, 2);
							const relatedExtra = relatedNames.length - relatedPreview.length;
							const relatedProductLabel = relatedPreview.join(' ');
							const relatedTitle = relatedNames.join(' · ');

							const noteItems = order.relatedNotes?.length ? order.relatedNotes : order.matchedNotes || [];
							const noteTexts = noteItems.map((item) => String(item?.note || '').trim()).filter(Boolean);
							const notePreview = noteTexts.slice(0, 2);
							const noteExtra = noteTexts.length - notePreview.length;
							const noteLabel = notePreview.join(' · ');
							const noteTitle = noteTexts.join(' · ');

							return (
								<div
									key={order.id}
									ref={(el) => {
										if (el) itemsRefs.current[globalIndex] = el;
									}}
									className={`global-search-table__row global-search-table__row--order${isSelected ? ' is-selected' : ''}`}
									onClick={(e) => {
										e.stopPropagation();
										onSelectOrder(order.id, order);
									}}
								>
									<span className='global-search-cell-ellipsis'>
										<span className='global-search-order-id-badge'>{highlightText(order.orderId)}</span>
									</span>
									<span className='global-search-cell-ellipsis' title={order.customerName || ''}>
										{highlightText(order.customerName || '—')}
									</span>
									<span className='global-search-related-products' title={relatedTitle || ''}>
										{relatedProductLabel ? (
											<>
												{highlightText(relatedProductLabel)}
												{relatedExtra > 0 ? (
													<span className='global-search-related-products__more'> +{relatedExtra}</span>
												) : null}
											</>
										) : (
											'—'
										)}
									</span>
									<span className='global-search-related-notes' title={noteTitle || ''}>
										{noteLabel ? (
											<>
												{highlightText(noteLabel)}
												{noteExtra > 0 ? <span className='global-search-related-products__more'> +{noteExtra}</span> : null}
											</>
										) : (
											'—'
										)}
									</span>
									<span className='global-search-table__status'>
										<SearchStatusPill statusBadges={statusBadges} />
										{showRemove ? (
											<button
												type='button'
												className='btn btn-sm btn-link global-search-item__remove'
												onClick={(e) => {
													e.stopPropagation();
													onRemoveHistoryItem('orders', order.id);
												}}
												title='删除此记录'
											>
												<FontAwesomeIcon icon='times' size='xs' />
											</button>
										) : null}
									</span>
									<span className='global-search-price'>
										{typeof highlightAmount === 'function' ? highlightAmount(order.totalPrice) : formatSearchPrice(order.totalPrice)}
									</span>
								</div>
							);
						})}
					</div>
				)}
			</SearchColumnCard>
		);
	};

	const renderCustomerColumn = () => {
		if (hideEmpty && customers.length === 0) return null;
		return (
			<SearchColumnCard
				key='customer'
				title='客户'
				count={customers.length}
				countSuffix=' 条'
				clearLabel={canClearCategory && customers.length > 0 ? '清空客户历史' : null}
				onClear={() => onClearCategoryHistory('customers')}
			>
				{customers.length === 0 ? (
					<SearchColumnEmpty text={emptyCustomerText} />
				) : (
					<div className='global-search-table global-search-table--customer'>
						<div className='global-search-table__head global-search-table__head--customer'>
							<span>客户名称</span>
							<span>城市</span>
							<span>电话</span>
							<span>类型</span>
						</div>
						{customers.map((customer, customerIndex) => {
							const globalIndex = getItemGlobalIndex('customer', customerIndex);
							const isSelected = selectedIndex === globalIndex;
							const cityLabel = getCityNameByCode(customer.city) || customer.city || '—';
							return (
								<div
									key={customer.id}
									ref={(el) => {
										if (el) itemsRefs.current[globalIndex] = el;
									}}
									className={`global-search-table__row global-search-table__row--customer${isSelected ? ' is-selected' : ''}`}
									onClick={(e) => {
										e.stopPropagation();
										onSelectCustomer(customer.id, customer);
									}}
								>
									<span className='global-search-cell-ellipsis' title={customer.name || ''}>
										{highlightText(customer.name)}
									</span>
									<span className='global-search-cell-ellipsis'>{cityLabel}</span>
									<span className='global-search-cell-ellipsis'>{customer.phone || '—'}</span>
									<span className='global-search-table__status'>
										<CustomerTypePill customerType={customer.customerType || customer.type} />
										{showRemove ? (
											<button
												type='button'
												className='btn btn-sm btn-link global-search-item__remove'
												onClick={(e) => {
													e.stopPropagation();
													onRemoveHistoryItem('customers', customer.id);
												}}
												title='删除此记录'
											>
												<FontAwesomeIcon icon='times' size='xs' />
											</button>
										) : null}
									</span>
								</div>
							);
						})}
					</div>
				)}
			</SearchColumnCard>
		);
	};

	const renderProductColumn = () => {
		if (hideEmpty && products.length === 0) return null;
		return (
			<SearchColumnCard
				key='product'
				title='产品'
				count={products.length}
				countSuffix=' 条'
				clearLabel={canClearCategory && products.length > 0 ? '清空产品历史' : null}
				onClear={() => onClearCategoryHistory('products')}
			>
				{products.length === 0 ? (
					<SearchColumnEmpty text={emptyProductText} />
				) : (
					<div className='global-search-table global-search-table--product'>
						<div className='global-search-table__head global-search-table__head--product'>
							<span>产品名称</span>
							<span>货号</span>
							<span>规格</span>
							<span>种类</span>
							<span className='global-search-table__num'>单价</span>
							<span>库存</span>
						</div>
						{products.map((product, productIndex) => {
							const globalIndex = getItemGlobalIndex('product', productIndex);
							const isSelected = selectedIndex === globalIndex;
							return (
								<div
									key={product.id}
									ref={(el) => {
										if (el) itemsRefs.current[globalIndex] = el;
									}}
									className={`global-search-table__row global-search-table__row--product${isSelected ? ' is-selected' : ''}`}
									onClick={(e) => {
										e.stopPropagation();
										onSelectProduct(product.id, product);
									}}
								>
									<span className='global-search-cell-ellipsis' title={product.name || ''}>
										{highlightText(product.name)}
									</span>
									<span className='global-search-sku'>{highlightText(product.productCode || '—')}</span>
									<span className='global-search-cell-ellipsis' title={product.specification || ''}>
										{highlightText(product.specification || '—')}
									</span>
									<span className='global-search-cell-ellipsis'>{product.category || '—'}</span>
									<span className='global-search-price'>{formatProductSearchPrice(product.minimumSellingPrice)}</span>
									<span className='global-search-table__status global-search-stock'>
										<span>{formatProductStockQty(product.stockQty)}</span>
										{showRemove ? (
											<button
												type='button'
												className='btn btn-sm btn-link global-search-item__remove'
												onClick={(e) => {
													e.stopPropagation();
													onRemoveHistoryItem('products', product.id);
												}}
												title='删除此记录'
											>
												<FontAwesomeIcon icon='times' size='xs' />
											</button>
										) : null}
									</span>
								</div>
							);
						})}
					</div>
				)}
			</SearchColumnCard>
		);
	};

	const renderers = {
		order: renderOrderColumn,
		customer: renderCustomerColumn,
		product: renderProductColumn,
	};

	return (
		<div className={`global-search-workbench${isStack ? ' global-search-workbench--stack' : ' global-search-workbench--columns'}`}>
			{sectionOrder.map((key) => renderers[key]?.())}
		</div>
	);
};


const SearchResultsCard = () => {
	const navigate = useNavigate();
	const { searchResults, searchKeyword, clearSearchResults, showSearchCard, isSearching, setShowSearchCardState } = useSearchContext();
	const [searchHistory, setSearchHistory] = useState(getSearchHistory());
	const { setCurrentOrderOIdState, order, isLoading: isOrderLoading, isOrderFetching } = useOrderContext();
	const [orderModalType, setOrderModalType] = useState(null);
	const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(-1);
	const itemsRefs = useRef([]);
	const positionCacheRef = useRef(new Map());
	const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
	const [selectedCustomerId, setSelectedCustomerId] = useState(null);
	const [selectedCustomerData, setSelectedCustomerData] = useState(null);
	const [isProductModalOpen, setIsProductModalOpen] = useState(false);
	const [selectedProductId, setSelectedProductId] = useState(null);
	const [selectedProductData, setSelectedProductData] = useState(null);

	// 当搜索卡片显示时，实时更新搜索历史
	useEffect(() => {
		if (showSearchCard) {
			setSearchHistory(getSearchHistory());
		}
	}, [showSearchCard]);

	// 高亮关键词（避免 /g + test 状态 bug）
	const highlightKeyword = useCallback((text, keyword) => {
		if (text == null || text === '' || !keyword) return text;
		const source = String(text);
		const escaped = String(keyword).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
		const regex = new RegExp(`(${escaped})`, 'gi');
		const parts = source.split(regex);
		if (parts.length === 1) return source;
		return parts.map((part, index) => {
			if (!part) return null;
			if (part.toLowerCase() === String(keyword).toLowerCase()) {
				return (
					<span key={index} className='global-search-highlight'>
						{part}
					</span>
				);
			}
			return <span key={index}>{part}</span>;
		});
	}, []);

	const highlightAmountValue = useCallback(
		(amount) => {
			const formatted = formatSearchPrice(amount);
			if (!searchKeyword || !isAmountMatchedByKeyword(amount, searchKeyword)) {
				return formatted;
			}
			return <span className='global-search-highlight'>{formatted}</span>;
		},
		[searchKeyword]
	);

	// 获取订单状态徽章
	const getOrderStatusBadges = useCallback((order) => {
		if (!order) return { delivery: null, payment: null };

		const { approvalStatus, deliveryStatus, paymentStatus, isPlaceOrderPaid, isDelivered, isAccepted, isCompleted, primaryPhase } = order;

		const getDeliveryStatusBadge = (order) => {
			// 待核验优先于已验收 / 误标已完成（与主阶段 pendingReview 一致）
			if (primaryPhase === 'pendingReview') {
				return { bg: 'primary', icon: 'clipboard-check', text: '待核验' };
			}
			if (isCompleted || primaryPhase === 'completed') {
				return { bg: 'success', icon: 'check-circle', text: '已完成' };
			}
			if (deliveryStatus === 'waitingDelivery' && !isDelivered) {
				return { bg: 'warning', icon: 'fa-truck-ramp-box', text: '待发货' };
			}
			if (deliveryStatus === 'waitingAccept' && isDelivered && !isAccepted) {
				return { bg: 'info', icon: 'fa-list-check', text: '待验收' };
			}
			if (isAccepted) {
				return { bg: 'success', icon: 'check', text: '已验收' };
			}
			return null;
		};

		const getPaymentStatusBadge = (order) => {
			if (isCompleted) return null;
			if (approvalStatus === 'inProcessOfApproval') {
				return { bg: 'warning', icon: 'clock', text: '待审批' };
			}
			if (paymentStatus === 'waitingPayPlaceOrder' && !isPlaceOrderPaid) {
				return { bg: 'warning', icon: 'dollar-sign', text: '待收预付款' };
			}
			if (paymentStatus === 'waitingPayDelivery') {
				return { bg: 'info', icon: 'dollar-sign', text: '待收发货款' };
			}
			if (paymentStatus === 'waitingPayAccept') {
				return { bg: 'info', icon: 'dollar-sign', text: '待收验收款' };
			}
			return null;
		};

		if (primaryPhase === 'pendingReview') {
			return {
				delivery: { bg: 'primary', icon: 'clipboard-check', text: '待核验' },
				payment: null,
			};
		}

		if (approvalStatus !== 'approved' && approvalStatus !== 'systemApproved') {
			return { delivery: null, payment: getPaymentStatusBadge(order) };
		}

		return {
			delivery: getDeliveryStatusBadge(order),
			payment: getPaymentStatusBadge(order),
		};
	}, []);

	// 获取订单状态优先级
	const getOrderStatusPriority = useCallback((order) => {
		if (!order) return 999;

		const { approvalStatus, deliveryStatus, paymentStatus, isPlaceOrderPaid, isDelivered, isAccepted, isCompleted, primaryPhase } = order;

		if (primaryPhase === 'pendingReview') {
			return 7;
		}

		if (isCompleted || primaryPhase === 'completed') {
			return 100;
		}

		if (approvalStatus === 'inProcessOfApproval') {
			return 1;
		}

		if (approvalStatus !== 'approved' && approvalStatus !== 'systemApproved') {
			return 90;
		}

		if (paymentStatus === 'waitingPayPlaceOrder' && !isPlaceOrderPaid) {
			return 2;
		}

		if (deliveryStatus === 'waitingDelivery' && !isDelivered) {
			return 3;
		}

		if (deliveryStatus === 'waitingAccept' && isDelivered && !isAccepted) {
			return 4;
		}

		if (paymentStatus === 'waitingPayDelivery') {
			return 5;
		}

		if (paymentStatus === 'waitingPayAccept') {
			return 6;
		}

		return 50;
	}, []);

	const matchPriority = searchResults?.matchPriority?.length ? searchResults.matchPriority : DEFAULT_MATCH_PRIORITY;

	// 有结果：按 matchPriority 扁平化；订单区内仍按工作流紧急度排序
	const allResults = useMemo(() => {
		if (!searchResults) return [];

		const sortedOrders =
			searchResults.orders?.length > 0
				? searchResults.orders
						.map((order) => ({
							...order,
							type: 'order',
							statusPriority: getOrderStatusPriority(order),
						}))
						.sort((a, b) => a.statusPriority - b.statusPriority)
						.map(({ statusPriority, ...order }) => order)
				: [];

		const sectionItems = {
			order: sortedOrders,
			customer: (searchResults.customers || []).map((customer) => ({ ...customer, type: 'customer' })),
			// 保留产品业务字段 category（设备/试剂/耗材/服务），分区用 type
			product: (searchResults.products || []).map((product) => ({ ...product, type: 'product' })),
		};

		const results = [];
		matchPriority.forEach((section) => {
			if (sectionItems[section]?.length) {
				results.push(...sectionItems[section]);
			}
		});
		return results;
	}, [searchResults, getOrderStatusPriority, matchPriority]);

	// 处理订单选择
	const handleSelectOrder = useCallback(
		async (orderId, orderData = null) => {
			if (orderData) {
				saveSearchHistory('orders', orderData);
				setSearchHistory(getSearchHistory());
			}

			if (orderData?.approvalStatus === 'reject') {
				navigate(getRejectedOrderEditPath(orderId));
				setShowSearchCardState(false);
				return;
			}

			savedSelectedIndexRef.current = selectedIndex;

			setCurrentOrderOIdState(orderId);
			socket.emit('join order', orderId);

			let initialModalType = 'processing';
			if (orderData && orderData.approvalStatus === 'inProcessOfApproval') {
				initialModalType = 'applying';
			}

			setOrderModalType(initialModalType);
			setIsOrderModalOpen(true);
		},
		[setCurrentOrderOIdState, selectedIndex, navigate, setShowSearchCardState]
	);

	// 监听订单数据变化，根据完整订单数据更新 modal 类型
	useEffect(() => {
		if (isOrderModalOpen && order && order._id && !isOrderLoading && !isOrderFetching) {
			const { approvalStatus, deliveryStatus, paymentStatus, isPlaceOrderPaid, isDelivered, isAccepted, isApproved } = order;

			let modalType = 'processing';
			if (approvalStatus === 'inProcessOfApproval') {
				modalType = 'applying';
			} else if (approvalStatus === 'reject') {
				navigate(getRejectedOrderEditPath(order._id));
				setIsOrderModalOpen(false);
				return;
			} else if ((approvalStatus === 'approved' || approvalStatus === 'systemApproved') && paymentStatus === 'waitingPayPlaceOrder' && !isPlaceOrderPaid) {
				modalType = 'prePayment';
			} else if (isPlaceOrderPaid && !isDelivered && deliveryStatus === 'waitingDelivery') {
				modalType = 'contract';
			} else if (isDelivered && !isAccepted && deliveryStatus === 'waitingAccept') {
				modalType = 'accept';
			} else if (isApproved && (paymentStatus === 'waitingPayDelivery' || paymentStatus === 'waitingPayAccept')) {
				modalType = 'ar';
			}

			if (modalType !== orderModalType) {
				setOrderModalType(modalType);
			}
		}
	}, [isOrderModalOpen, order, isOrderLoading, isOrderFetching, orderModalType, navigate]);

	// 使用ref跟踪modal之前的状态，以检测关闭事件
	const prevOrderModalOpenRef = useRef(false);
	const prevCustomerModalOpenRef = useRef(false);
	const prevProductModalOpenRef = useRef(false);
	// 保存点击打开modal前的导航索引，以便关闭后恢复
	const savedSelectedIndexRef = useRef(-1);
	// 标记是否正在恢复导航索引，防止被搜索结果改变时的重置逻辑覆盖
	const isRestoringIndexRef = useRef(false);

	// 当 Modal 关闭时，更新搜索历史并重新聚焦搜索框（因为可能已经保存了新的访问记录）
	// 这个函数会在modal关闭时被调用，无论关闭方式（ESC键、点击关闭按钮等）
	const handleModalClose = useCallback(() => {
		// 恢复之前的导航索引 - 先设置恢复标志，防止索引被重置
		if (savedSelectedIndexRef.current >= 0) {
			isRestoringIndexRef.current = true;

			// 更新搜索历史以反映最新的访问记录
			const updatedHistory = getSearchHistory();
			setSearchHistory(updatedHistory);

			// 延迟恢复索引，确保搜索历史状态已更新并且 allNavigableItems 已重新计算
			setTimeout(() => {
				// 计算当前的 allNavigableItems
				const currentShowHistory = !searchKeyword || (!searchResults && !isSearching && searchKeyword.length < 2);
				let currentItemsCount = 0;

				if (currentShowHistory) {
					// 使用最新的搜索历史
					const latestHistory = getSearchHistory();
					currentItemsCount = (latestHistory.orders?.length || 0) + (latestHistory.customers?.length || 0) + (latestHistory.products?.length || 0);
				} else {
					// 使用搜索结果
					currentItemsCount = allResults.length;
				}

				// 如果 allNavigableItems 已经有数据，直接恢复索引
				if (currentItemsCount > 0 && savedSelectedIndexRef.current >= 0) {
					const savedIndex = savedSelectedIndexRef.current;
					if (savedIndex >= 0 && savedIndex < currentItemsCount) {
						requestAnimationFrame(() => {
							setSelectedIndex(savedIndex);
							setTimeout(() => {
								isRestoringIndexRef.current = false;
								savedSelectedIndexRef.current = -1;
							}, 200);
						});
					}
				}
			}, 300);
		} else {
			// 如果没有保存的索引，直接更新搜索历史
			setSearchHistory(getSearchHistory());
		}

		// 重新聚焦搜索框并保持搜索卡片显示
		setTimeout(() => {
			const searchInput = document.querySelector('.search-input');
			if (searchInput) {
				// 确保搜索卡片保持显示
				setShowSearchCardState(true);
				// 确保搜索关键词和结果保持不变（如果存在）
				// 重新聚焦搜索框，这会触发 GlobalSearchBox 的同步逻辑
				searchInput.focus();
			}
		}, 250);
	}, [setShowSearchCardState, searchKeyword, searchResults, isSearching, allResults]);

	// 监听订单modal关闭
	useEffect(() => {
		// 检测modal从打开变为关闭
		if (prevOrderModalOpenRef.current && !isOrderModalOpen) {
			handleModalClose();
		}
		prevOrderModalOpenRef.current = isOrderModalOpen;
	}, [isOrderModalOpen, handleModalClose]);

	// 监听客户modal关闭
	useEffect(() => {
		// 检测modal从打开变为关闭
		if (prevCustomerModalOpenRef.current && !isCustomerModalOpen) {
			handleModalClose();
		}
		prevCustomerModalOpenRef.current = isCustomerModalOpen;
	}, [isCustomerModalOpen, handleModalClose]);

	// 监听产品modal关闭
	useEffect(() => {
		// 检测modal从打开变为关闭
		if (prevProductModalOpenRef.current && !isProductModalOpen) {
			handleModalClose();
		}
		prevProductModalOpenRef.current = isProductModalOpen;
	}, [isProductModalOpen, handleModalClose]);

	const handleSelectCustomer = useCallback(
		(customerId, customerData = null) => {
			if (customerData) {
				saveSearchHistory('customers', customerData);
				// 更新搜索历史状态
				setSearchHistory(getSearchHistory());
			}
			// 保存当前的导航索引，以便modal关闭后恢复
			savedSelectedIndexRef.current = selectedIndex;
			setSelectedCustomerId(customerId);
			setSelectedCustomerData(customerData);
			setIsCustomerModalOpen(true);
		},
		[selectedIndex]
	);

	const handleSelectProduct = useCallback(
		(productId, productData = null) => {
			if (productData) {
				saveSearchHistory('products', productData);
				// 更新搜索历史状态
				setSearchHistory(getSearchHistory());
			}
			// 保存当前的导航索引，以便modal关闭后恢复
			savedSelectedIndexRef.current = selectedIndex;
			setSelectedProductId(productId);
			setSelectedProductData(productData);
			setIsProductModalOpen(true);
		},
		[selectedIndex]
	);

	// 如果没有搜索关键词或正在搜索，显示搜索历史
	const showHistory = !searchKeyword || (!searchResults && !isSearching && searchKeyword.length < 2);
	const hasHistory = searchHistory.orders?.length > 0 || searchHistory.customers?.length > 0 || searchHistory.products?.length > 0;

	// 处理删除单个历史记录
	const handleRemoveHistoryItem = useCallback((category, itemId) => {
		removeSearchHistoryItem(category, itemId);
		setSearchHistory(getSearchHistory());
	}, []);

	// 处理清空某个类别的历史记录
	const handleClearCategoryHistory = useCallback((category) => {
		if (window.confirm(`确定要清空所有${category === 'orders' ? '订单' : category === 'customers' ? '客户' : '产品'}的访问历史吗？`)) {
			clearSearchHistory(category);
			setSearchHistory(getSearchHistory());
		}
	}, []);

	// 处理清空所有历史记录
	const handleClearAllHistory = useCallback(() => {
		if (window.confirm('确定要清空所有访问历史吗？此操作不可恢复。')) {
			clearSearchHistory();
			setSearchHistory(getSearchHistory());
		}
	}, []);

	const orders = showHistory ? [] : allResults.filter((item) => item.type === 'order');
	const customers = showHistory ? [] : allResults.filter((item) => item.type === 'customer');
	const products = showHistory ? [] : allResults.filter((item) => item.type === 'product');
	const hasResults = orders.length > 0 || customers.length > 0 || products.length > 0;
	const resultCountLabels = { order: '订单', customer: '客户', product: '产品' };
	const resultCountValues = {
		order: searchResults?.stats?.orders || 0,
		customer: searchResults?.stats?.customers || 0,
		product: searchResults?.stats?.products || 0,
	};
	const resultCountSummary = matchPriority
		.filter((key) => resultCountValues[key] > 0)
		.map((key) => `${resultCountValues[key]} 个${resultCountLabels[key]}`);
	const resultCountSummaryText = resultCountSummary.length > 0 ? ` · ${resultCountSummary.join(' · ')}` : '';
	const hitResultCap =
		!showHistory &&
		hasResults &&
		[resultCountValues.order, resultCountValues.customer, resultCountValues.product].some((n) => n >= GLOBAL_SEARCH_RESULT_LIMIT);

	// 创建扁平化的所有可导航项数组（用于键盘导航）
	const allNavigableItems = useMemo(() => {
		if (showHistory) {
			const items = [];
			if (searchHistory.orders) {
				searchHistory.orders.forEach((order) => items.push({ ...order, type: 'order' }));
			}
			if (searchHistory.customers) {
				searchHistory.customers.forEach((customer) => items.push({ ...customer, type: 'customer' }));
			}
			if (searchHistory.products) {
				searchHistory.products.forEach((product) => items.push({ ...product, type: 'product' }));
			}
			return items;
		}
		return allResults;
	}, [showHistory, searchHistory, allResults]);

	// 当 allNavigableItems 更新且正在恢复索引时，验证并恢复索引
	useEffect(() => {
		if (isRestoringIndexRef.current && savedSelectedIndexRef.current >= 0) {
			if (allNavigableItems.length > 0) {
				const savedIndex = savedSelectedIndexRef.current;
				// 验证索引是否在有效范围内
				if (savedIndex >= 0 && savedIndex < allNavigableItems.length) {
					// 使用 requestAnimationFrame 确保 DOM 已更新
					requestAnimationFrame(() => {
						requestAnimationFrame(() => {
							setSelectedIndex(savedIndex);
							// 延迟重置标志，确保索引设置完成
							setTimeout(() => {
								isRestoringIndexRef.current = false;
								savedSelectedIndexRef.current = -1;
							}, 150);
						});
					});
				} else if (allNavigableItems.length > 0) {
					// 如果索引无效，尝试选中第一个
					requestAnimationFrame(() => {
						requestAnimationFrame(() => {
							setSelectedIndex(0);
							setTimeout(() => {
								isRestoringIndexRef.current = false;
								savedSelectedIndexRef.current = -1;
							}, 150);
						});
					});
				}
			} else {
				// 如果 allNavigableItems 为空，稍后重试（等待最多 600ms）
				const timer = setTimeout(() => {
					if (isRestoringIndexRef.current && savedSelectedIndexRef.current >= 0 && allNavigableItems.length > 0) {
						const savedIndex = savedSelectedIndexRef.current;
						if (savedIndex >= 0 && savedIndex < allNavigableItems.length) {
							requestAnimationFrame(() => {
								setSelectedIndex(savedIndex);
								isRestoringIndexRef.current = false;
								savedSelectedIndexRef.current = -1;
							});
						}
					}
				}, 600);
				return () => clearTimeout(timer);
			}
		}
	}, [allNavigableItems]);

	// 最近访问固定 订单→客户→产品；有结果跟随 matchPriority
	const getItemGlobalIndex = useCallback(
		(category, localIndex) => {
			if (showHistory) {
				const orderCount = searchHistory.orders?.length || 0;
				const customerCount = searchHistory.customers?.length || 0;
				if (category === 'order') return localIndex;
				if (category === 'customer') return orderCount + localIndex;
				if (category === 'product') return orderCount + customerCount + localIndex;
				return localIndex;
			}

			const sectionCounts = {
				order: orders.length,
				customer: customers.length,
				product: products.length,
			};
			let offset = 0;
			for (const section of matchPriority) {
				if (section === category) return offset + localIndex;
				offset += sectionCounts[section] || 0;
			}
			return localIndex;
		},
		[showHistory, searchHistory, orders.length, customers.length, products.length, matchPriority]
	);

	// 获取基于位置的导航目标索引（用于宫格视图）
	const getPositionBasedIndex = useCallback((currentIndex, direction, itemsCount, itemsPerRow = 3) => {
		if (currentIndex < 0) {
			// 如果没有选中项，从第一个开始
			return direction === 'ArrowDown' || direction === 'ArrowRight' ? 0 : -1;
		}

		if (currentIndex >= itemsCount) return itemsCount - 1;

		// 获取当前项的DOM元素位置
		const currentElement = itemsRefs.current[currentIndex];
		if (!currentElement) {
			// 如果无法获取元素位置，回退到顺序导航
			if (direction === 'ArrowDown') return Math.min(itemsCount - 1, currentIndex + 1);
			if (direction === 'ArrowUp') return Math.max(0, currentIndex - 1);
			if (direction === 'ArrowRight') return Math.min(itemsCount - 1, currentIndex + 1);
			if (direction === 'ArrowLeft') return Math.max(0, currentIndex - 1);
			return currentIndex;
		}

		const currentRect = currentElement.getBoundingClientRect();
		if (currentRect.width === 0 || currentRect.height === 0) {
			// 元素不可见，回退到顺序导航
			if (direction === 'ArrowDown') return Math.min(itemsCount - 1, currentIndex + 1);
			if (direction === 'ArrowUp') return Math.max(0, currentIndex - 1);
			if (direction === 'ArrowRight') return Math.min(itemsCount - 1, currentIndex + 1);
			if (direction === 'ArrowLeft') return Math.max(0, currentIndex - 1);
			return currentIndex;
		}

		const currentCenterX = currentRect.left + currentRect.width / 2;
		const currentCenterY = currentRect.top + currentRect.height / 2;

		// 增加容差，使用元素宽高的较大值作为容差
		const toleranceX = Math.max(currentRect.width * 1.5, 50);
		const toleranceY = Math.max(currentRect.height * 1.5, 50);

		let bestMatch = -1;
		let bestDistance = Infinity;
		let bestAlignment = Infinity;

		// 辅助函数：检查元素是否匹配方向
		const checkElement = (element, index, rect) => {
			if (!element || index === currentIndex || rect.width === 0 || rect.height === 0) {
				return null;
			}

			const centerX = rect.left + rect.width / 2;
			const centerY = rect.top + rect.height / 2;

			let isCandidate = false;
			let alignment = 0;
			let distance = 0;

			switch (direction) {
				case 'ArrowUp':
					if (centerY < currentCenterY && Math.abs(centerX - currentCenterX) < toleranceX) {
						isCandidate = true;
						alignment = Math.abs(centerX - currentCenterX);
						distance = currentCenterY - centerY;
					}
					break;
				case 'ArrowDown':
					if (centerY > currentCenterY && Math.abs(centerX - currentCenterX) < toleranceX) {
						isCandidate = true;
						alignment = Math.abs(centerX - currentCenterX);
						distance = centerY - currentCenterY;
					}
					break;
				case 'ArrowLeft':
					if (centerX < currentCenterX && Math.abs(centerY - currentCenterY) < toleranceY) {
						isCandidate = true;
						alignment = Math.abs(centerY - currentCenterY);
						distance = currentCenterX - centerX;
					}
					break;
				case 'ArrowRight':
					if (centerX > currentCenterX && Math.abs(centerY - currentCenterY) < toleranceY) {
						isCandidate = true;
						alignment = Math.abs(centerY - currentCenterY);
						distance = centerX - currentCenterX;
					}
					break;
				default:
					break;
			}

			if (isCandidate) {
				return { alignment, distance, index };
			}
			return null;
		};

		// 遍历所有项，找到最近的目标项（优化：优先检查附近的元素）
		for (let index = 0; index < itemsRefs.current.length; index++) {
			const element = itemsRefs.current[index];
			if (!element) continue;

			const rect = element.getBoundingClientRect();
			const candidate = checkElement(element, index, rect);

			if (candidate) {
				// 优先选择对齐度最好的，然后选择距离最近的
				if (candidate.alignment < bestAlignment || (candidate.alignment === bestAlignment && candidate.distance < bestDistance)) {
					bestMatch = candidate.index;
					bestAlignment = candidate.alignment;
					bestDistance = candidate.distance;
				}
			}
		}

		// 如果找到匹配项，返回；否则回退到顺序导航
		if (bestMatch >= 0) {
			return bestMatch;
		}

		// 回退到顺序导航
		switch (direction) {
			case 'ArrowDown':
				return Math.min(itemsCount - 1, currentIndex + itemsPerRow);
			case 'ArrowUp':
				return Math.max(0, currentIndex - itemsPerRow);
			case 'ArrowRight':
				return Math.min(itemsCount - 1, currentIndex + 1);
			case 'ArrowLeft':
				return Math.max(0, currentIndex - 1);
			default:
				return currentIndex;
		}
	}, []);

	// 键盘导航处理
	useEffect(() => {
		if (!showSearchCard) {
			return;
		}

		const handleKeyDown = (e) => {
			// 如果modal打开，不处理ESC键（让modal自己处理）
			if (e.key === 'Escape' && (isOrderModalOpen || isCustomerModalOpen || isProductModalOpen)) {
				return;
			}

			// Escape 键关闭搜索卡片（仅在modal未打开时）
			if (e.key === 'Escape') {
				e.preventDefault();
				e.stopPropagation();
				setSelectedIndex(-1);
				clearSearchResults();
				return;
			}

			// 如果没有可导航项，不处理其他按键
			if (allNavigableItems.length === 0) {
				return;
			}

			// 只处理箭头键和Enter
			if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(e.key)) {
				return;
			}

			const activeElement = document.activeElement;
			const isInputFocused = activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'TEXTAREA' || activeElement?.isContentEditable;

			// 检查是否是搜索输入框（通过class名判断）
			const isSearchInput = activeElement?.classList?.contains('search-input');

			// 如果焦点在搜索输入框，允许键盘导航
			if (isInputFocused && isSearchInput) {
				// 对于Enter键，如果已经选中了某个结果项，打开该项
				if (e.key === 'Enter') {
					if (selectedIndex >= 0 && selectedIndex < allNavigableItems.length) {
						e.preventDefault();
						e.stopPropagation();
						const item = allNavigableItems[selectedIndex];
						if (item.type === 'order') {
							handleSelectOrder(item.id, item);
						} else if (item.type === 'customer') {
							handleSelectCustomer(item.id, item);
						} else if (item.type === 'product') {
							handleSelectProduct(item.id, item);
						}
						return;
					}
					// 如果没有选中项，允许输入框的默认行为（提交表单等）
					return;
				}

				// 对于箭头键，在搜索输入框聚焦时也允许导航（阻止输入框光标移动）
				e.preventDefault();
				e.stopPropagation();
			} else if (isInputFocused && !isSearchInput) {
				// 如果焦点在其他输入框或可编辑元素中（非搜索输入框），不允许导航
				return;
			} else {
				// 如果焦点不在输入框中，正常处理导航
				e.preventDefault();
				e.stopPropagation();
			}

			// 三栏工作台：按屏幕位置导航（上下同行、左右跨列）
			if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
				setSelectedIndex((prev) => {
					const currentIndex = prev < 0 ? 0 : prev;
					return getPositionBasedIndex(currentIndex, e.key, allNavigableItems.length, 1);
				});
			}

			if (e.key === 'Enter') {
				if (selectedIndex >= 0 && selectedIndex < allNavigableItems.length) {
					const item = allNavigableItems[selectedIndex];
					if (item.type === 'order') {
						handleSelectOrder(item.id, item);
					} else if (item.type === 'customer') {
						handleSelectCustomer(item.id, item);
					} else if (item.type === 'product') {
						handleSelectProduct(item.id, item);
					}
				}
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [
		showSearchCard,
		allNavigableItems,
		selectedIndex,
		getPositionBasedIndex,
		handleSelectOrder,
		handleSelectCustomer,
		handleSelectProduct,
		clearSearchResults,
		isOrderModalOpen,
		isCustomerModalOpen,
		isProductModalOpen,
	]);

	// 滚动到选中项
	useEffect(() => {
		if (selectedIndex >= 0 && itemsRefs.current[selectedIndex]) {
			// 使用 requestAnimationFrame 确保DOM已更新
			requestAnimationFrame(() => {
				const element = itemsRefs.current[selectedIndex];
				if (element) {
					element.scrollIntoView({
						behavior: 'smooth',
						block: 'nearest',
					});
				}
			});
		}
	}, [selectedIndex]);

	// 当搜索结果或历史记录改变时，重置选中索引和缓存（但如果modal打开或正在恢复索引，保留索引）
	useEffect(() => {
		if (showSearchCard && !isOrderModalOpen && !isCustomerModalOpen && !isProductModalOpen && !isRestoringIndexRef.current) {
			// 只有在没有modal打开且不在恢复索引时才重置索引
			setSelectedIndex(-1);
			// 使用更高效的方式清理refs
			if (allNavigableItems.length > 0) {
				itemsRefs.current = new Array(allNavigableItems.length);
			} else {
				itemsRefs.current = [];
			}
			// 清理位置缓存
			positionCacheRef.current.clear();
		}
	}, [showHistory, searchResults, searchHistory, showSearchCard, allNavigableItems.length, isOrderModalOpen, isCustomerModalOpen, isProductModalOpen]);

	// 如果没有显示搜索卡片的标志，不显示
	if (!showSearchCard) return null;

	return (
		<>
			<div
				className={`global-search-panel${
					(!showHistory && hasResults) || (showHistory && hasHistory) ? ' global-search-panel--workbench' : ''
				}`}
			>
				<div className='global-search-panel__header'>
					<div className='min-w-0 flex-grow-1'>
						<h5 className='global-search-panel__title'>
							{showHistory ? (
								'最近访问'
							) : (
								<>
									搜索结果：<span className='global-search-panel__keyword'>{searchKeyword}</span>
								</>
							)}
						</h5>
						{!showHistory && searchResults?.stats ? (
							<div className='global-search-panel__meta'>
								共 {searchResults.stats.total} 条{resultCountSummaryText}
								{hitResultCap ? <span className='global-search-panel__meta-cap'>（每类最多 {GLOBAL_SEARCH_RESULT_LIMIT} 条）</span> : null}
							</div>
						) : null}
					</div>
					{showHistory && hasHistory ? (
						<div className='global-search-panel__actions'>
							<button type='button' className='btn btn-sm btn-outline-danger' onClick={handleClearAllHistory}>
								清空全部
							</button>
						</div>
					) : null}
				</div>

				<div className='global-search-panel__body'>
					{showHistory ? (
						!hasHistory ? (
							<div className='global-search-empty'>
								<FontAwesomeIcon icon='clock-rotate-left' className='global-search-empty__icon' />
								<p className='global-search-empty__title'>暂无搜索历史</p>
								<p className='global-search-empty__desc'>搜索订单、客户或产品后，访问记录将显示在这里</p>
							</div>
						) : (
							<SearchWorkbenchColumns
								layout='columns'
								sectionOrder={['order', 'customer', 'product']}
								orders={searchHistory.orders || []}
								customers={searchHistory.customers || []}
								products={searchHistory.products || []}
								selectedIndex={selectedIndex}
								getItemGlobalIndex={getItemGlobalIndex}
								itemsRefs={itemsRefs}
								getOrderStatusBadges={getOrderStatusBadges}
								highlightText={(text) => text}
								highlightAmount={(amount) => formatSearchPrice(amount)}
								onSelectOrder={handleSelectOrder}
								onSelectCustomer={handleSelectCustomer}
								onSelectProduct={handleSelectProduct}
								onRemoveHistoryItem={handleRemoveHistoryItem}
								onClearCategoryHistory={handleClearCategoryHistory}
								emptyOrderText='暂无访问订单'
								emptyCustomerText='暂无访问客户'
								emptyProductText='暂无访问产品'
							/>
						)
					) : isSearching ? (
						<div className='global-search-empty'>
							<FontAwesomeIcon icon='spinner' spin className='global-search-empty__icon text-primary' />
							<p className='global-search-empty__title'>正在搜索...</p>
						</div>
					) : !hasResults ? (
						<div className='global-search-empty'>
							<FontAwesomeIcon icon='search' className='global-search-empty__icon' />
							<p className='global-search-empty__title'>未找到相关结果</p>
							<p className='global-search-empty__desc'>试试订单号、客户名或产品名/货号</p>
						</div>
					) : (
						<SearchWorkbenchColumns
							layout='stack'
							sectionOrder={matchPriority}
							orders={orders}
							customers={customers}
							products={products}
							selectedIndex={selectedIndex}
							getItemGlobalIndex={getItemGlobalIndex}
							itemsRefs={itemsRefs}
							getOrderStatusBadges={getOrderStatusBadges}
							highlightText={(text) => highlightKeyword(text, searchKeyword)}
							highlightAmount={highlightAmountValue}
							onSelectOrder={handleSelectOrder}
							onSelectCustomer={handleSelectCustomer}
							onSelectProduct={handleSelectProduct}
							emptyOrderText='无匹配订单'
							emptyCustomerText='无匹配客户'
							emptyProductText='无匹配产品'
						/>
					)}
				</div>
			</div>

			{/* 订单 Modal 组件 */}
			{orderModalType === 'applying' && <ApplyingModal setIsOpenPendingModal={setIsOrderModalOpen} isOpenPendingModal={isOrderModalOpen} onHide={handleModalClose} />}
			{orderModalType === 'prePayment' && (
				<PrePaymentModal setIsOpenPendingModal={setIsOrderModalOpen} isOpenPendingModal={isOrderModalOpen} onHide={() => setSearchHistory(getSearchHistory())} />
			)}
			{orderModalType === 'contract' && (
				<ContractModal setIsOpenPendingModal={setIsOrderModalOpen} isOpenPendingModal={isOrderModalOpen} onHide={() => setSearchHistory(getSearchHistory())} />
			)}
			{orderModalType === 'delivery' && (
				<DeliveryModal setIsOpenPendingModal={setIsOrderModalOpen} isOpenPendingModal={isOrderModalOpen} onHide={() => setSearchHistory(getSearchHistory())} />
			)}
			{orderModalType === 'accept' && (
				<AcceptModal setIsOpenPendingModal={setIsOrderModalOpen} isOpenPendingModal={isOrderModalOpen} onHide={() => setSearchHistory(getSearchHistory())} />
			)}
			{orderModalType === 'ar' && (
				<AccountsReceivableModal setIsOpenPendingModal={setIsOrderModalOpen} isOpenPendingModal={isOrderModalOpen} onHide={() => setSearchHistory(getSearchHistory())} />
			)}
			{orderModalType === 'processing' && (
				<ProcessingOrderModal setIsOpenPendingModal={setIsOrderModalOpen} isOpenPendingModal={isOrderModalOpen} onHide={() => setSearchHistory(getSearchHistory())} />
			)}
			{orderModalType === 'approve' && (
				<ApproveModal setIsOpenPendingModal={setIsOrderModalOpen} isOpenPendingModal={isOrderModalOpen} onHide={() => setSearchHistory(getSearchHistory())} />
			)}

			{/* 客户 Modal 组件 */}
			<CustomerModal
				setIsOpenModal={setIsCustomerModalOpen}
				isOpenModal={isCustomerModalOpen}
				customerId={selectedCustomerId}
				customerData={selectedCustomerData}
				onHide={handleModalClose}
			/>

			{/* 产品 Modal 组件 */}
			<ProductModal
				setIsOpenModal={setIsProductModalOpen}
				isOpenModal={isProductModalOpen}
				productId={selectedProductId}
				productData={selectedProductData}
				onHide={handleModalClose}
			/>
		</>
	);
};

export default SearchResultsCard;
